/**
 * Common Composable
 * 管理通用功能（公告、廣播、設定等）
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/core/common.js)
 * - API：使用 useFetch 支援 SSR，透過 onResponse 同步處理資料
 * - 單例模式：使用 nuxtApp._commonContext 避免 storeToRefs 重複創建
 */

import { storeToRefs } from 'pinia'
import { UAParser } from 'ua-parser-js'
import { ref, watch, onBeforeUnmount, getCurrentInstance } from 'vue'

import { useFetch, useRuntimeConfig, useNuxtApp } from '#app'
import { commonApi } from '@/api/common'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useSite } from '@/composables/core/useSite'
import { useCommonStore } from '@/stores/core/common'
import { resolveReferralKpix, resolveReferralPix } from '@/utils/referral'

const FEED_POLL_INTERVAL = 20000 // 20 seconds
const FBC_COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // 7 days
const MAX_BROADCAST_HISTORY = 100 // 最多保留 100 條廣播記錄

export const useCommon = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useFetch 和 storeToRefs 只創建一次
  if (!nuxtApp._commonContext) {
    const commonStore = useCommonStore()
    const { $request } = nuxtApp
    const config = useRuntimeConfig()
    const { apiUrl, clientId, appSecret, intranet } = config.public
    const { getSiteConfig } = useSite()

    // storeToRefs 只調用一次
    const storeRefs = storeToRefs(commonStore)

    // SSR-safe: 非共享狀態
    const userAgent = ref('')
    const isMobile = ref(false)
    const normalBroadcast = ref(null)
    const sentNormalBroadcastList = ref([])
    const isPlaying = ref(false)
    const isLogin = ref(false)
    const pix = ref('')
    const kpix = ref('')

    // Timer context
    const ctx = {
      feedPollTimer: null,
      broadcastTimer: null,
    }

    // Watch stop 函數陣列，用於 cleanup 時停止所有 watch
    const stopWatchers = []

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 使用 useFetch 自動取得公告（只創建一次）
    const announcementsApiConfig = commonApi.getAnnouncements({ appSecret })
    const announcementsApiPath = announcementsApiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 使用 onResponse 確保 SSR 階段資料同步處理，避免 Hydration 時序問題
    useFetch(`${baseUrl}/${announcementsApiPath}`, {
      key: 'announcements',
      dedupe: 'defer',
      query: announcementsApiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
      onResponse({ response }) {
        if (response._data?.data) {
          commonStore.setAnnouncementList(response._data.data)
        }
      },
      onResponseError({ error: err }) {
        console.error('[useCommon] fetchAnnouncements API error:', err)
      },
    })

    const setUA = () => {
      const parser = new UAParser()
      const result = parser.getResult()
      userAgent.value = result
      isMobile.value = !!result?.device?.type
    }

    const setPixel = () => {
      if (typeof window === 'undefined') return
      const pixValue = resolveReferralPix() || ''
      if (pixValue !== '') {
        pix.value = pixValue
      }
    }

    const setKPixel = () => {
      if (typeof window === 'undefined') return
      const kpixValue = resolveReferralKpix() || ''
      if (kpixValue !== '') {
        kpix.value = kpixValue
      }
    }

    const fetchConfig = async () => {
      try {
        setUA()
        setPixel()
        setKPixel()
        setFbclid()
      } catch (error) {
        console.error('[useCommon] fetchConfig error:', error)
        throw error
      }
    }

    /**
     * Set Facebook Click ID (_fbc cookie)
     */
    const setFbclid = () => {
      // SSR 環境跳過
      if (typeof window === 'undefined') {
        return
      }

      const { params: urlParams } = useUrlParams(['fbclid'])
      // 使用 watch 來監聽 urlParams 的變化
      // 因為 useUrlParams 是在 onMounted 後才會有值
      stopWatchers.push(
        watch(urlParams, (newParams) => {
          if (newParams?.fbclid) {
            const creationTime = Date.now()
            const fbcValue = `fb.1.${creationTime}.${newParams.fbclid}`
            const fbcCookie = useCookie('_fbc', {
              maxAge: FBC_COOKIE_MAX_AGE,
              sameSite: 'lax',
            })
            fbcCookie.value = fbcValue
          }
        })
      )
    }

    /**
     * Send pixel
     */
    const fetchSendPixel = async ({ eventId, event }) => {
      const eventSourceUrl = window.location.href
      const fbc = useCookie('_fbc').value || ''
      const fbp = useCookie('_fbp').value || ''

      // [2026/05/27] Sentry 上已有數據可參考
      // [2026/05/27] eventSourceUrl 缺失率也已下降
      // [2026/05/27] fbp 沒有下降，但因為 fbp 是廣告帶的參數，推測玩家可能是從網址有帶 pixel 的連結，或是網址存在我的最愛，但因為不是真的從FB廣告點進來等原因所以本身沒有 fbp 資料
      // [2026/05/27] 所以決定先移除診斷與 Sentry 回報，不佔用 Sentry 資源
      if (!appSecret || !eventSourceUrl) {
        return
      }

      try {
        const result = await $request.send(commonApi.sendPixel({ appSecret, eventId, fbc, fbp, event, eventSourceUrl }))
        return result
      } catch (error) {
        console.error('[useCommon] sendPixel error:', error)
        throw error
      }
    }

    const fetchFeeds = async () => {
      try {
        const result = await $request.send(commonApi.feeds({ appSecret }))
        commonStore.setBroadcastList(result.broadcastList)

        const customerMessageEnable = getSiteConfig.value?.csChat?.some((item) => item.enable && item.name === 'customermessage')
        if (!customerMessageEnable && result.mailBoxInfo) {
          result.mailBoxInfo.rows -= result.mailBoxInfo.detailRows?.CUSTOMER || 0
        }
        commonStore.setMailBoxInfo(result.mailBoxInfo)

        return result
      } catch (error) {
        console.error('[useCommon] fetchFeeds error:', error)
        throw error
      }
    }

    const fetchAnnouncements = async () => {
      try {
        const result = await $request.send(commonApi.getAnnouncements({ appSecret }))
        commonStore.setAnnouncementList(result.data)
        return result
      } catch (error) {
        console.error('[useCommon] fetchAnnouncements error:', error)
        throw error
      }
    }

    const clearAnnouncements = () => {
      commonStore.clearAnnouncementList()
    }

    const startFeedPolling = () => {
      if (!import.meta.client) return
      if (ctx.feedPollTimer) {
        clearInterval(ctx.feedPollTimer)
      }
      ctx.feedPollTimer = setInterval(() => {
        if (!isPlaying.value) {
          // 背景輪詢用：吞掉 rejection，避免 unhandledrejection 上報 Sentry（CLIENT-PWA-SSR-1AF）
          // fetchFeeds 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪（20s）自動重試
          fetchFeeds().catch(() => {})
        }
      }, FEED_POLL_INTERVAL)
    }

    const stopFeedPolling = () => {
      if (!import.meta.client) return
      if (ctx.feedPollTimer) {
        clearInterval(ctx.feedPollTimer)
        ctx.feedPollTimer = null
      }
    }

    const setNextBroadcast = () => {
      let sentList = sentNormalBroadcastList.value
      if (normalBroadcast.value) {
        sentList = [...sentNormalBroadcastList.value, normalBroadcast.value]
        // 限制陣列大小，保留最新的 MAX_BROADCAST_HISTORY 個項目
        if (sentList.length > MAX_BROADCAST_HISTORY) {
          sentList = sentList.slice(-MAX_BROADCAST_HISTORY)
        }
        sentNormalBroadcastList.value = sentList
      }

      const nextBroadcast = storeRefs.broadcastList.value?.broadcasts?.find((broadcast) => {
        return broadcast.type === '0' && !sentList.some((item) => item.broadcast_id === broadcast.broadcast_id)
      })

      normalBroadcast.value = nextBroadcast || null
    }

    const resetBroadcast = () => {
      normalBroadcast.value = null
      sentNormalBroadcastList.value = []
    }

    const startBroadcast = () => {
      if (!import.meta.client) return
      if (ctx.broadcastTimer) {
        clearInterval(ctx.broadcastTimer)
      }
      ctx.broadcastTimer = setInterval(() => {
        setNextBroadcast()
      }, 10000)
    }

    const stopBroadcast = () => {
      if (!import.meta.client) return
      if (ctx.broadcastTimer) {
        clearInterval(ctx.broadcastTimer)
        ctx.broadcastTimer = null
      }
      resetBroadcast()
    }

    const logout = () => {
      isLogin.value = false
      commonStore.clearBroadcastList()
      commonStore.clearMailBoxInfo()
      stopFeedPolling()
    }

    const startGameSession = () => {
      isPlaying.value = true
      commonStore.clearBroadcastList()
      commonStore.clearMailBoxInfo()
      stopFeedPolling()
    }

    const endGameSession = async () => {
      isPlaying.value = false
      if (isLogin.value) {
        startFeedPolling()
        // 背景刷新用：吞掉 rejection，避免 unhandledrejection 上報 Sentry（CLIENT-PWA-SSR-16E），
        // 並確保呼叫端 onBeforeUnmount 的後續清理（activityRefresh / leaveGame 等）不被中斷；
        // fetchFeeds 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪輪詢自動重試
        await fetchFeeds().catch(() => {})
      }
    }

    const fetchDefaultAffiliateCode = async () => {
      try {
        const result = await $request.send(commonApi.getDefaultAffiliateCode({ appSecret }))
        commonStore.setDefaultAffiliateCode(result.affiliateCode)
      } catch (error) {
        console.error('[useCommon] fetchDefaultAffiliateCode error:', error)
        throw error
      }
    }

    const initialize = async () => {
      try {
        await fetchConfig()
      } catch (error) {
        console.error('[useCommon] initialize error:', error)
      }
    }

    // Timer + watch 清理方法
    const cleanup = () => {
      // 停止所有 watch
      stopWatchers.forEach((stop) => stop?.())
      // 清理 timer
      if (ctx.feedPollTimer) {
        clearInterval(ctx.feedPollTimer)
        ctx.feedPollTimer = null
      }
      if (ctx.broadcastTimer) {
        clearInterval(ctx.broadcastTimer)
        ctx.broadcastTimer = null
      }
    }

    nuxtApp._commonContext = {
      // State
      userAgent,
      isMobile,
      announcementList: storeRefs.announcementList,
      broadcastList: storeRefs.broadcastList,
      mailBoxInfo: storeRefs.mailBoxInfo,
      normalBroadcast,
      isPlaying,
      isLogin,
      pix,
      kpix,
      defaultAffiliateCode: storeRefs.defaultAffiliateCode,

      // Methods
      initialize,
      fetchConfig,
      fetchFeeds,
      startFeedPolling,
      stopFeedPolling,
      fetchAnnouncements,
      clearAnnouncements,
      setUA,
      setPixel,
      setKPixel,
      logout,
      startGameSession,
      endGameSession,
      startBroadcast,
      stopBroadcast,
      resetBroadcast,
      fetchDefaultAffiliateCode,
      fetchSendPixel,
      cleanup,
      stopWatchers,
      // Internal (for client-side watch)
      _isLogin: isLogin,
      _clientWatchCreated: false,
    }
  }

  const ctx = nuxtApp._commonContext

  // 客戶端專屬：同步 isLogin 狀態
  // 每次 useCommon() 呼叫時直接同步，確保狀態一致
  if (import.meta.client) {
    const { isLoggedIn } = useAuthToken()
    // 直接同步 isLogin 狀態
    ctx._isLogin.value = isLoggedIn.value
    if (isLoggedIn.value) {
      ctx.startFeedPolling()
      // 背景觸發用：吞掉 rejection，避免 unhandledrejection 上報 Sentry（CLIENT-PWA-SSR-16E）
      // fetchFeeds 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪輪詢自動重試
      ctx.fetchFeeds().catch(() => {})
    }

    // 創建 watch 監聽後續變化（如果還沒創建）
    if (!ctx._clientWatchCreated) {
      ctx.stopWatchers.push(
        watch(isLoggedIn, (value) => {
          ctx._isLogin.value = value
          if (value) {
            ctx.startFeedPolling()
            // 背景觸發用：吞掉 rejection，避免 unhandledrejection 上報 Sentry（CLIENT-PWA-SSR-16E）
            // fetchFeeds 內已 console.error；真實錯誤已於 request 層上報，失敗等下一輪輪詢自動重試
            ctx.fetchFeeds().catch(() => {})
          }
        })
      )
      ctx._clientWatchCreated = true
    }
  }

  // 元件卸載時自動清理 timer（每次調用都需要檢查）
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      ctx.stopFeedPolling()
      ctx.stopBroadcast()
    })
  }

  return ctx
}
